import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiEnvelope,
  BlogPost,
  Client,
  PrivacyPolicy,
  Project,
  Service,
  SiteSettings,
} from './models';

/**
 * Read-only content from the public API (projects, services, clients, settings).
 * GET requests are cached with shareReplay so the home page and inner pages
 * don't refetch the same lists.
 */
@Injectable({ providedIn: 'root' })
export class ContentService {
  private http = inject(HttpClient);
  private base = environment.apiBase;

  private projects$?: Observable<Project[]>;
  private services$?: Observable<Service[]>;
  private clients$?: Observable<Client[]>;
  private blogs$?: Observable<BlogPost[]>;
  private policy$?: Observable<PrivacyPolicy>;

  getProjects(): Observable<Project[]> {
    if (!this.projects$) {
      this.projects$ = this.http
        .get<ApiEnvelope<{ projects: Project[] }>>(`${this.base}/projects`)
        .pipe(
          map((r) => r.data?.projects ?? []),
          shareReplay(1),
        );
    }
    return this.projects$;
  }

  getProject(id: string): Observable<Project> {
    return this.http
      .get<ApiEnvelope<{ project: Project }>>(`${this.base}/projects/${id}`)
      .pipe(map((r) => r.data.project));
  }

  getServices(): Observable<Service[]> {
    if (!this.services$) {
      this.services$ = this.http
        .get<ApiEnvelope<{ services: Service[] }>>(`${this.base}/services`)
        .pipe(
          map((r) => (r.data?.services ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
          shareReplay(1),
        );
    }
    return this.services$;
  }

  getService(slug: string): Observable<Service> {
    return this.http
      .get<ApiEnvelope<{ service: Service }>>(`${this.base}/services/${slug}`)
      .pipe(map((r) => r.data.service));
  }

  getClients(): Observable<Client[]> {
    if (!this.clients$) {
      this.clients$ = this.http
        .get<ApiEnvelope<{ clients: Client[] }>>(`${this.base}/clients`)
        .pipe(
          map((r) => (r.data?.clients ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
          shareReplay(1),
        );
    }
    return this.clients$;
  }

  getBlogs(): Observable<BlogPost[]> {
    if (!this.blogs$) {
      this.blogs$ = this.http
        .get<ApiEnvelope<{ posts: BlogPost[] }>>(`${this.base}/blogs`)
        .pipe(
          map((r) => r.data?.posts ?? []),
          shareReplay(1),
        );
    }
    return this.blogs$;
  }

  getBlog(slug: string): Observable<BlogPost> {
    return this.http
      .get<ApiEnvelope<{ post: BlogPost }>>(`${this.base}/blogs/${slug}`)
      .pipe(map((r) => r.data.post));
  }

  getSettings(): Observable<SiteSettings> {
    return this.http
      .get<ApiEnvelope<{ settings: SiteSettings }>>(`${this.base}/settings`)
      .pipe(map((r) => r.data?.settings ?? {}));
  }

  getPolicy(): Observable<PrivacyPolicy> {
    if (!this.policy$) {
      this.policy$ = this.http
        .get<ApiEnvelope<{ policy: PrivacyPolicy }>>(`${this.base}/policy`)
        .pipe(
          map((r) => r.data.policy),
          shareReplay(1),
        );
    }
    return this.policy$;
  }

  /** Bust the caches (used after admin mutations). */
  invalidate() {
    this.projects$ = undefined;
    this.services$ = undefined;
    this.clients$ = undefined;
    this.blogs$ = undefined;
    this.policy$ = undefined;
  }
}
